/**
 * Modal de l'export du rapport : télécharger, et accessoirement clôturer.
 *
 * La case est DÉCOCHÉE par défaut — l'export est le geste courant, la clôture
 * l'exception mensuelle. Cocher fige le mois précédent : plus aucun salarié ne
 * pourra créer, modifier ni supprimer une saisie de ce mois-là (ni des mois
 * antérieurs), quel que soit le mode de saisie. Seul un admin reste libre.
 */
import { useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { isMonthKeyLocked } from '@altyor-kronos/shared-types';

/** 'YYYY-MM' → « août 2026 ». */
export function formatMonthKey(key: string, locale: string): string {
  const [y, m] = key.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString(locale, { month: 'long', year: 'numeric' });
}

interface Props {
  open: boolean;
  /** Mois que la validation figerait, 'YYYY-MM'. */
  monthToClose: string;
  /** Clôture déjà en place, 'YYYY-MM' ou null. */
  lockedThrough: string | null;
  onCancel: () => void;
  /** `close` = l'utilisateur a coché la case. L'export part dans les deux cas. */
  onConfirm: (close: boolean) => void;
}

export default function CloseMonthDialog({
  open,
  monthToClose,
  lockedThrough,
  onCancel,
  onConfirm,
}: Props) {
  const { t, i18n } = useTranslation();
  const [close, setClose] = useState(false);

  // Déjà figé : on n'offre pas de refigeage, l'export reste disponible.
  const already = isMonthKeyLocked(monthToClose, lockedThrough);
  const monthLabel = formatMonthKey(monthToClose, i18n.language);

  const handleCancel = () => { setClose(false); onCancel(); };
  const handleConfirm = () => { onConfirm(close); setClose(false); };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle>{t('kronos.report.closeMonth.title')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t('kronos.report.closeMonth.intro')}
        </Typography>

        {already ? (
          <Alert severity="info" sx={{ borderRadius: 0 }}>
            {t('kronos.report.closeMonth.already', {
              month: formatMonthKey(lockedThrough as string, i18n.language),
            })}
          </Alert>
        ) : (
          <>
            <FormControlLabel
              control={
                <Checkbox
                  checked={close}
                  onChange={(e) => setClose(e.target.checked)}
                  data-testid="close-month-checkbox"
                />
              }
              label={t('kronos.report.closeMonth.checkbox', { month: monthLabel })}
            />
            {close && (
              <Alert severity="warning" sx={{ mt: 1, borderRadius: 0 }}>
                {t('kronos.report.closeMonth.warning', { month: monthLabel })}
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          data-testid="close-month-confirm"
        >
          {close
            ? t('kronos.report.closeMonth.confirmAndClose')
            : t('kronos.report.closeMonth.confirmExportOnly')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
